import { useMemo, useState } from 'react';
import { Modal, Button } from '../primitives';
import { useCompanies, useCreateCompany, useCreateContact } from '../../api/hooks';
import type { Company, CompanyCreate } from '../../api/schemas';
import styles from './CompanyPickerButton.module.css';

/**
 * Tlačítko „👤 Kontakty" — výběr kontaktní osoby z adresáře.
 *
 * Zdrojem jsou osoby uložené v adresáři firem (Company.contacts[] + primární
 * kontakt firmy). Umožňuje také uložit novou osobu k vybrané firmě
 * (POST /companies/{id}/contacts). Když firma z dokumentu v adresáři ještě
 * není, založí se rovnou (POST /companies/) a osoba se přiřadí k ní —
 * dřív to končilo hláškou „Vyberte firmu" a nešlo uložit.
 *
 * Záměrně se kontaktní osoby NEPŘEDVYPLŇUJÍ v dokumentech — uživatel si je
 * buď napíše ručně, nebo vyvolá tímto pickerem (viz požadavek kolegyně).
 */

export interface ContactPerson {
  jmeno: string;
  funkce: string;
  tel: string;
  email: string;
}

interface AggregatedContact extends ContactPerson {
  key: string;
  firma: string;
}

export interface ContactPickerButtonProps {
  onPick: (p: ContactPerson) => void;
  /** Předvyplnění formuláře „uložit nového" ze současného stavu řádku. */
  draftNew?: Partial<ContactPerson>;
  /** Název firmy, ke které novou osobu přednostně uložit (dohledá se v adresáři). */
  attachToCompany?: string;
  /**
   * Další údaje firmy z dokumentu (IČO, adresa, role…) pro případ, že firma
   * v adresáři není a musí se založit. Název se bere z `attachToCompany`.
   */
  companyDraft?: Partial<Omit<CompanyCreate, 'nazev'>>;
  label?: string;
  compact?: boolean;
  title?: string;
  disabled?: boolean;
}

function collectContacts(companies: Company[]): AggregatedContact[] {
  const out: AggregatedContact[] = [];
  const seen = new Set<string>();
  const push = (firma: string, jmeno: string, funkce: string, tel: string, email: string) => {
    const jm = (jmeno ?? '').trim();
    if (!jm) return;
    const k = `${jm.toLocaleLowerCase('cs')}|${(tel ?? '').trim()}|${(email ?? '').trim()}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ key: `${out.length}-${k}`, firma, jmeno: jm, funkce: funkce ?? '', tel: tel ?? '', email: email ?? '' });
  };
  for (const c of companies) {
    if (c.kontakt_jmeno) push(c.nazev ?? '', c.kontakt_jmeno, '', c.kontakt_tel ?? '', c.kontakt_email ?? '');
    if (c.stavbyvedouci) push(c.nazev ?? '', c.stavbyvedouci, 'stavbyvedoucí', c.stavbyvedouci_tel ?? '', '');
    for (const k of c.contacts ?? []) {
      push(c.nazev ?? '', k.jmeno, k.pozice ?? '', k.tel ?? '', k.email ?? '');
    }
  }
  out.sort((a, b) => a.jmeno.localeCompare(b.jmeno, 'cs'));
  return out;
}

const NEW_COMPANY = 'new' as const;

export function ContactPickerButton({
  onPick,
  draftNew,
  attachToCompany,
  companyDraft,
  label = 'Kontakty',
  compact,
  title,
  disabled,
}: ContactPickerButtonProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'pick' | 'new'>('pick');
  const [q, setQ] = useState('');
  const { data: companies = [], isLoading, error } = useCompanies();
  const createContact = useCreateContact();
  const createCompany = useCreateCompany();

  const contacts = useMemo(() => collectContacts(companies), [companies]);

  const filtered = useMemo(() => {
    const query = q.trim().toLocaleLowerCase('cs');
    if (!query) return contacts;
    return contacts.filter((c) =>
      [c.jmeno, c.funkce, c.firma, c.email, c.tel]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('cs')
        .includes(query),
    );
  }, [contacts, q]);

  // Formulář „uložit nového"
  const [nJmeno, setNJmeno] = useState('');
  const [nFunkce, setNFunkce] = useState('');
  const [nTel, setNTel] = useState('');
  const [nEmail, setNEmail] = useState('');
  const [nCompanyId, setNCompanyId] = useState<number | '' | typeof NEW_COMPANY>('');
  const [nCompanyName, setNCompanyName] = useState('');
  const [saveMsg, setSaveMsg] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  function openModal() {
    setQ('');
    setSaveMsg(null);
    setNJmeno(draftNew?.jmeno ?? '');
    setNFunkce(draftNew?.funkce ?? '');
    setNTel(draftNew?.tel ?? '');
    setNEmail(draftNew?.email ?? '');
    const wanted = (attachToCompany ?? '').trim();
    const match = wanted
      ? companies.find((c) => (c.nazev ?? '').trim().toLocaleLowerCase('cs') === wanted.toLocaleLowerCase('cs'))
      : undefined;
    // Firma z dokumentu v adresáři není → rovnou nabídnout její založení.
    setNCompanyId(match ? match.id : wanted ? NEW_COMPANY : '');
    setNCompanyName(wanted);
    setTab(draftNew?.jmeno ? 'new' : 'pick');
    setOpen(true);
  }

  function choose(c: AggregatedContact) {
    onPick({ jmeno: c.jmeno, funkce: c.funkce, tel: c.tel, email: c.email });
    setOpen(false);
  }

  async function handleSaveNew() {
    setSaveMsg(null);
    if (!nJmeno.trim()) {
      setSaveMsg({ kind: 'err', msg: 'Vyplňte jméno osoby.' });
      return;
    }
    if (!nCompanyId) {
      setSaveMsg({ kind: 'err', msg: 'Vyberte firmu, ke které osobu uložit, nebo založte novou.' });
      return;
    }
    if (nCompanyId === NEW_COMPANY && !nCompanyName.trim()) {
      setSaveMsg({ kind: 'err', msg: 'Vyplňte název nové firmy.' });
      return;
    }
    try {
      let companyId: number;
      if (nCompanyId === NEW_COMPANY) {
        const created = await createCompany.mutateAsync({
          role: 'oba',
          ...companyDraft,
          nazev: nCompanyName.trim(),
        });
        companyId = created.id;
      } else {
        companyId = nCompanyId;
      }
      await createContact.mutateAsync({
        companyId,
        data: { jmeno: nJmeno.trim(), pozice: nFunkce.trim(), tel: nTel.trim(), email: nEmail.trim() },
      });
      // Rovnou dosadit do dokumentu a zavřít.
      onPick({ jmeno: nJmeno.trim(), funkce: nFunkce.trim(), tel: nTel.trim(), email: nEmail.trim() });
      setOpen(false);
    } catch (e) {
      setSaveMsg({ kind: 'err', msg: e instanceof Error ? e.message : 'Chyba při ukládání.' });
    }
  }

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={openModal}
        disabled={disabled}
        title={title ?? 'Vybrat kontaktní osobu z adresáře'}
      >
        <span aria-hidden>👤</span>
        {!compact && <span>{label}</span>}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="lg"
        title="Kontaktní osoba"
        footer={
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Zavřít
          </Button>
        }
      >
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <button
            type="button"
            className={styles.trigger}
            style={tab === 'pick' ? activeTab : undefined}
            onClick={() => setTab('pick')}
          >
            Vybrat z adresáře
          </button>
          <button
            type="button"
            className={styles.trigger}
            style={tab === 'new' ? activeTab : undefined}
            onClick={() => setTab('new')}
          >
            + Uložit novou
          </button>
        </div>

        {tab === 'pick' ? (
          <>
            <input
              className={styles.search}
              placeholder="Hledat osobu podle jména, firmy, e-mailu…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
            {isLoading ? (
              <div className={styles.empty}>Načítám…</div>
            ) : error ? (
              <div className={styles.statusErr}>
                Chyba: {error instanceof Error ? error.message : 'nelze načíst'}
              </div>
            ) : filtered.length === 0 ? (
              <div className={styles.empty}>
                {contacts.length === 0
                  ? 'V adresáři zatím nejsou žádné kontaktní osoby. Přidejte je přes „+ Uložit novou".'
                  : `Žádný výsledek pro „${q.trim()}".`}
              </div>
            ) : (
              <div className={styles.list}>
                {filtered.map((c) => (
                  <button key={c.key} type="button" className={styles.item} onClick={() => choose(c)}>
                    <div className={styles.itemMain}>
                      <div className={styles.itemName}>{c.jmeno}</div>
                      <div className={styles.itemMeta}>
                        {c.funkce && <span>{c.funkce}</span>}
                        {c.tel && <span className={styles.mono}>{c.tel}</span>}
                        {c.email && <span>{c.email}</span>}
                      </div>
                      {c.firma && <div className={styles.itemContact}>{c.firma}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {saveMsg && (
              <div className={saveMsg.kind === 'ok' ? styles.statusOk : styles.statusErr}>{saveMsg.msg}</div>
            )}
            <label style={fieldLabel}>
              Jméno
              <input style={fieldInput} value={nJmeno} onChange={(e) => setNJmeno(e.target.value)} placeholder="Jan Novák" autoFocus />
            </label>
            <label style={fieldLabel}>
              Funkce
              <input style={fieldInput} value={nFunkce} onChange={(e) => setNFunkce(e.target.value)} placeholder="stavbyvedoucí" />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ ...fieldLabel, flex: 1 }}>
                Telefon
                <input style={fieldInput} value={nTel} onChange={(e) => setNTel(e.target.value)} placeholder="+420…" />
              </label>
              <label style={{ ...fieldLabel, flex: 1 }}>
                E-mail
                <input style={fieldInput} value={nEmail} onChange={(e) => setNEmail(e.target.value)} placeholder="email@…" />
              </label>
            </div>
            <label style={fieldLabel}>
              Uložit k firmě
              <select
                style={fieldInput}
                value={nCompanyId}
                onChange={(e) => {
                  const v = e.target.value;
                  setNCompanyId(v === NEW_COMPANY ? NEW_COMPANY : v ? Number(v) : '');
                }}
              >
                <option value="">— vyberte firmu z adresáře —</option>
                <option value={NEW_COMPANY}>
                  {attachToCompany?.trim() ? `➕ Založit novou firmu „${attachToCompany.trim()}"` : '➕ Založit novou firmu'}
                </option>
                {[...companies]
                  .sort((a, b) => (a.nazev ?? '').localeCompare(b.nazev ?? '', 'cs'))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nazev || '— bez názvu —'}
                    </option>
                  ))}
              </select>
            </label>
            {nCompanyId === NEW_COMPANY && (
              <label style={fieldLabel}>
                Název nové firmy
                <input
                  style={fieldInput}
                  value={nCompanyName}
                  onChange={(e) => setNCompanyName(e.target.value)}
                  placeholder="Stavební firma s.r.o."
                />
                <span style={{ fontWeight: 400 }}>
                  Firma se založí v adresáři
                  {companyDraft?.ico?.trim() ? ` (IČO ${companyDraft.ico.trim()})` : ''}
                  {companyDraft?.adresa?.trim() ? `, ${companyDraft.adresa.trim()}` : ''}
                  {' '}a osoba se uloží k ní.
                </span>
              </label>
            )}
            {companies.length === 0 && nCompanyId !== NEW_COMPANY && (
              <div className={styles.empty} style={{ padding: '12px 0' }}>
                Adresář je prázdný — vyberte „➕ Založit novou firmu", nebo firmu uložte přes „💾".
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                onClick={handleSaveNew}
                disabled={createContact.isPending || createCompany.isPending}
              >
                {createContact.isPending || createCompany.isPending ? 'Ukládám…' : 'Uložit a vložit'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

const activeTab: React.CSSProperties = {
  borderColor: 'var(--sd-yellow-border)',
  color: 'var(--sd-yellow-text)',
  background: 'var(--sd-yellow-soft)',
};

const fieldLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 11.5,
  fontWeight: 600,
  color: 'var(--sd-text-dim)',
};

const fieldInput: React.CSSProperties = {
  font: 'inherit',
  fontSize: 13,
  color: 'var(--sd-text)',
  background: 'var(--sd-surface-2)',
  border: '1px solid var(--sd-border)',
  borderRadius: 6,
  padding: '8px 10px',
  height: 36,
  outline: 'none',
};
